import * as BasketUpdated from '@ori-events/basket-updated';
import * as PlaceOrderRequested from '@ori-events/place-order-requested';
import * as RequestClearShoppingCache from '@ori-events/request-clear-shopping-cache';
import * as RequestShoppingContext from '@ori-events/request-shopping-context';
import * as ShoppingContextLoaded from '@ori-events/shopping-context-loaded';
import { useToken } from '@ori/auth';
import { getDebugLogger } from '@ori/checkout-utils';
import { useApiClient } from '@ori/global-api-client-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { APP_NAME, CONTEXT_KEY } from '../constants';
import { shoppingContextActions } from '../constants/shoppingContextActions';
import { useConfiguration } from '../contexts';
import type { HandlerSettings, ContextUpdateEventHandler, SuccessEventName } from '../types';
import {
  dispatchBasketUpdatedEvent,
  getStorageItem,
  isPageMarkedForCacheCleanup,
  isScheduledCacheCleanup,
  isStorageAvailable,
  listenToStoredItemChanges,
  logReceivedEvent,
  removeStorageItem,
  removeScheduledCacheCleanup,
  setStorageItem,
} from '../utils';

const logDebug = getDebugLogger(APP_NAME);

let isReadyPromiseResolve: (value: PromiseLike<boolean> | boolean) => void;

/**
 * Promise indicating that shopping bag provider handlers are connected
 * and other components can start communicating with the provider via events.
 */
export const isReady = new Promise<boolean>((resolve) => {
  isReadyPromiseResolve = resolve;
});

type EventListenerInfo = {
  eventName: string;
  eventListener: EventListener;
};

const getCachedContext = () => getStorageItem<BasketUpdated.Data>(CONTEXT_KEY);
const setCachedContext = (value: BasketUpdated.Data) => setStorageItem(CONTEXT_KEY, value);
const clearCachedContext = () => removeStorageItem(CONTEXT_KEY);

export const useBasketProviderData = () => {
  const client = useApiClient();
  const { tenant } = useConfiguration();
  const { customerId } = useToken();

  // Indication that startup shopping context data initialization was done.
  const isInitialShoppingContextApplied = useRef(false);

  // Requesting shopping context data usually on application startup has now
  // specific logic involving also this state flag. When true, subsequent coming
  // request-shopping-context events will not trigger additional server requests
  // & it waits for the 1st request to return.
  const isRequestingShoppingContext = useRef(false);

  // Current shopping context / shopping session data. This is also being synchronized
  // with the cache on startup & after each update action.
  const [shoppingContext, setShoppingContext] = useState<BasketUpdated.Data>();

  const shoppingSessionId = useRef('');

  /**
   * Sets current shopping context data state while optionally setting also the cache
   * and/or triggering a requested success response event.
   */
  const updateContext = useCallback(
    (data: BasketUpdated.Data, eventTypeToBeTriggered?: SuccessEventName, shouldStoreData = true) => {
      shoppingSessionId.current = data.sessionId ?? '';
      setShoppingContext(data);
      if (shouldStoreData && isStorageAvailable()) {
        setCachedContext(data);
        logDebug('Stored context was updated with new data:\n\t', data);
      } else {
        logDebug('Context was updated with new data:\n\t', data);
      }
      if (eventTypeToBeTriggered) {
        logDebug(`Triggering ${eventTypeToBeTriggered} event ...`);
        dispatchBasketUpdatedEvent(data, eventTypeToBeTriggered);
      }
    },
    [],
  );

  /** Tries to read last cache and, if found, sets it back into current shopping context state. */
  const getStoredContext = useCallback(() => {
    const data = getCachedContext();
    if (!data) {
      return;
    }
    updateContext(data, undefined, false);
    logDebug('Restored context data from storage.');
  }, [updateContext]);

  /** Clears current shopping context data and stored cache. */
  const clearContext = useCallback(() => {
    logDebug('Clearing stored shopping context data ...');
    clearCachedContext();
    setShoppingContext(undefined);
  }, []);

  /** Clears shopping context data based on received event. */
  const clearContextOnEvent = useCallback(
    (event: Event) => {
      logReceivedEvent(event);
      clearContext();
    },
    [clearContext],
  );

  /** Generic event listener for most update actions. */
  const createEventListener = useCallback(
    (handler: ContextUpdateEventHandler, currentSettings: HandlerSettings) => {
      const result = (async <T extends CustomEvent>(event: T) => {
        logReceivedEvent(event);
        const data = await handler(event, currentSettings);
        if (data === false) {
          logDebug('Shopping context update failed.');
        } else {
          updateContext(data, BasketUpdated.Name);
        }
      }) as EventListener;

      return result;
    },
    [updateContext],
  );

  /**
   * Specific event listener for request-shopping-context action.
   * Getting the shopping context data from storage and/or the isRequestingShoppingContext
   * state is involved in this case. If in cache, the data is returned w/o creating
   * another request to server. If the loading already started before, it waits for
   * the original request to return the data.
   */
  const createRequestShoppingContextEventListener = useCallback(
    (handler: ContextUpdateEventHandler, currentSettings: HandlerSettings) => {
      const result = (async <T extends CustomEvent>(event: T) => {
        logReceivedEvent(event);

        if (shoppingContext) {
          const triggerResponse = () => {
            logDebug(`Context data was loaded before. Triggering ${ShoppingContextLoaded.Name} event ...`);
            dispatchBasketUpdatedEvent(shoppingContext, ShoppingContextLoaded.Name);
          };

          // postpone a bit to have same events order as when really fetching the data
          setTimeout(() => triggerResponse());

          return;
        }

        if (isRequestingShoppingContext.current) {
          logDebug('Shopping context was already requested. Waiting for response ...');

          return;
        }

        logDebug('Loading shopping context ...');
        isRequestingShoppingContext.current = true;
        const data = await handler(event, currentSettings);
        isRequestingShoppingContext.current = false;
        if (data === false) {
          logDebug('Shopping context load failed.');
        } else {
          logDebug('Shopping context was loaded.');
          updateContext(data, ShoppingContextLoaded.Name);
        }
      }) as EventListener;

      return result;
    },
    [isRequestingShoppingContext, shoppingContext, updateContext],
  );

  /** Creates event listeners for the actions defined in shoppingContextActions list. */
  const getEventListeners = useCallback(
    (currentSettings: HandlerSettings) => {
      const result: EventListenerInfo[] = [];
      for (const [triggerActionEventName, { handler }] of shoppingContextActions.entries()) {
        const eventListenerFactory =
          triggerActionEventName === RequestShoppingContext.Name
            ? createRequestShoppingContextEventListener
            : createEventListener;
        result.push({
          eventName: triggerActionEventName,
          eventListener: eventListenerFactory(handler, currentSettings),
        });
      }

      return result;
    },
    [createEventListener, createRequestShoppingContextEventListener],
  );

  // setup initial state of shopping context data
  useEffect(() => {
    if (isStorageAvailable() && !isInitialShoppingContextApplied.current) {
      isInitialShoppingContextApplied.current = true;
      if (isPageMarkedForCacheCleanup() || isScheduledCacheCleanup()) {
        removeScheduledCacheCleanup();
        clearContext();
      } else {
        getStoredContext();
      }
    }
  }, [clearContext, getStoredContext]);

  // init event listeners
  useEffect(() => {
    const eventListeners = getEventListeners({ client, customerId, sessionId: shoppingSessionId.current, tenant });
    eventListeners.push(
      // This is now called when the user requests creating a new order.
      // That means that the cache should be cleared. But we don't know yet
      // if the create order action succeeds. Getting exact order creation
      // result / success might be problematic, as there are page redirects
      // involved, and after page reload, it is too late for clearing caches.
      // So current solution calls the PlaceOrderRequested event before
      // calling the request on server, so that there is enough time to
      // do this cache cleanup here before the redirects happen.
      // ATM, the redirect is being done in legacy module & this logic might
      // change after rewriting to React.
      {
        eventName: PlaceOrderRequested.Name,
        eventListener: clearContextOnEvent,
      },

      // This is now called for other cases when clearing context data is needed.
      {
        eventName: RequestClearShoppingCache.Name,
        eventListener: clearContextOnEvent,
      },
    );

    // If there was a change in stored data from another browser tab,
    // if the context value was set, update the context with triggering
    // basket-updated event. Other components need to be informed about
    // the new state & this will not happen on page load, but later.
    // So the basket-updated event should be fine for this.
    if (isStorageAvailable()) {
      const monitoredStorageChanges = listenToStoredItemChanges<BasketUpdated.Data>(CONTEXT_KEY, (value) => {
        if (value) {
          updateContext(value, BasketUpdated.Name, false);
        }
      });
      if (monitoredStorageChanges) {
        const { eventName, eventListener } = monitoredStorageChanges;
        eventListeners.push({ eventName, eventListener });
      }
    }

    eventListeners.forEach(({ eventName, eventListener }) => {
      window.addEventListener(eventName, eventListener);
    });
    logDebug('Event listeners are connected.');

    // TODO: We publish the promise to window scope & resolve it immediately.
    // Is it worth using promise at all when polling is still needed in consuming components?
    // Maybe the exported isReady promise on module level is still worth it?
    window.isBasketProviderReady = isReady;
    isReadyPromiseResolve(true);

    return () => {
      eventListeners.forEach(({ eventName, eventListener }) => {
        window.removeEventListener(eventName, eventListener);
      });
      eventListeners.length = 0;
      logDebug('Old event listeners were disconnected.');
    };
  }, [clearContextOnEvent, client, customerId, getEventListeners, tenant, updateContext]);

  const contextValue = useMemo(
    () => ({
      isRequestingShoppingContext,
      isReady,
      shoppingContext,
    }),
    [isRequestingShoppingContext, shoppingContext],
  );

  return contextValue;
};
